import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { clubsApi, playersApi, transferRequestsApi, usersApi, contractsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { EAStatsSection } from '../components/EAStatsSection';
import { ClubAdvancedStats } from '../components/ClubAdvancedStats';
import { premiumApi } from '../lib/api';
import {
  Shield,
  Plus,
  Upload,
  Users,
  Coins,
  Goal,
  CircleDot,
  AlertCircle,
  UserPlus,
  UserMinus,
  Check,
  X,
  Gamepad2,
  LogOut,
  Search,
  FileText,
  Send,
  Lock,
  Unlock,
  ShoppingCart,
  Crown,
  Star,
} from 'lucide-react';

const Club = () => {
  const { t, i18n } = useTranslation();
  const { user, refreshUser, isCaptain } = useAuth();
  const [club, setClub] = useState(null);
  const [players, setPlayers] = useState([]);
  const [transferRequests, setTransferRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [releaseCost, setReleaseCost] = useState(50000);
  const [myContractEnd, setMyContractEnd] = useState(null);
  const [contractExpired, setContractExpired] = useState(false);
  const [releaseInfo, setReleaseInfo] = useState({ payment_source: 'club', message: '' });
  const [releasing, setReleasing] = useState(false);
  const [clubForm, setClubForm] = useState({ name: '', primaryColor: '#2979FF', secondaryColor: '#FFFFFF' });
  const [playerForm, setPlayerForm] = useState({ name: '', position: 'MID', number: 10 });
  
  // State for player recruitment (new feature)
  const [searchQuery, setSearchQuery] = useState('');
  const [freeAgents, setFreeAgents] = useState([]);
  const [searchingPlayers, setSearchingPlayers] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [contractDurations, setContractDurations] = useState([]);
  const [contractForm, setContractForm] = useState({ duration: '6_months' });
  const [sendingOffer, setSendingOffer] = useState(false);
  
  // State for accepting transfer request with contract
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [acceptingRequest, setAcceptingRequest] = useState(false);
  
  // State for releasing a player (captain action)
  const [showReleasePlayerDialog, setShowReleasePlayerDialog] = useState(false);
  const [playerToRelease, setPlayerToRelease] = useState(null);
  const [releasingPlayer, setReleasingPlayer] = useState(false);
  
  // State for vice captain
  const [settingViceCaptain, setSettingViceCaptain] = useState(false);

  // State for logo change cost
  const [logoChangeCount, setLogoChangeCount] = useState(0);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [showLogoCostDialog, setShowLogoCostDialog] = useState(false);
  const LOGO_CHANGE_COST = 100000;

  const isItalian = i18n.language === 'it';
  const isViceUser = !!user?.user_id && [club?.vice_captain_id, club?.vice_captain_id_2].includes(user.user_id);

  const fetchClubData = useCallback(async () => {
    try {
      let clubData = null;
      let clubId = user?.club_id;
      
      // If user has club_id, fetch directly
      if (clubId) {
        const clubRes = await clubsApi.get(clubId);
        clubData = clubRes.data;
      } else if (isCaptain || user?.role === 'super_admin') {
        // If captain or super_admin without club_id, find club by captain_id
        const clubsRes = await clubsApi.list();
        clubData = clubsRes.data.find(c => c.captain_id === user?.user_id);
        if (clubData) {
          clubId = clubData.club_id;
        }
      }
      
      if (!clubData) {
        setLoading(false);
        return;
      }
      
      setClub(clubData);

      // Fetch logo change cost
      if (isCaptain || user?.role === 'captain') {
        try {
          const costRes = await clubsApi.getLogoCost(clubId);
          setLogoChangeCount(costRes.data.change_count || 0);
        } catch (err) { /* ignore */ }
      }
      // Fetch club members (users with this club_id)
      try {
        const membersRes = await clubsApi.getMembers(clubId);
        setPlayers(membersRes.data);
      } catch (err) {
        console.error('Error fetching members:', err);
        setPlayers([]);
      }

      // Fetch transfer requests if captain, vice captain or super_admin
      if (isCaptain || user?.role === 'super_admin' || [clubData?.vice_captain_id, clubData?.vice_captain_id_2].includes(user?.user_id)) {
        try {
          const requestsRes = await transferRequestsApi.getClubRequests(clubId);
          setTransferRequests(requestsRes.data);
        } catch (err) {
          console.error('Error fetching transfer requests:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching club:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.club_id, user?.user_id, user?.role, isCaptain]);

  useEffect(() => {
    fetchClubData();
  }, [fetchClubData]);

  // Fetch release cost
  useEffect(() => {
    const fetchReleaseCost = async () => {
      try {
        const res = await clubsApi.getReleaseCost();
        setReleaseCost(res.data.cost);
        setMyContractEnd(res.data.contract_end || null);
        setContractExpired(!!res.data.contract_expired);
        setReleaseInfo({
          payment_source: res.data.payment_source,
          message: res.data.message
        });
      } catch (err) {
        console.error('Error fetching release cost:', err);
      }
    };
    fetchReleaseCost();
  }, []);

  // Fetch contract durations
  useEffect(() => {
    const fetchDurations = async () => {
      try {
        const res = await contractsApi.getDurations();
        setContractDurations(res.data);
      } catch (err) {
        console.error('Error fetching contract durations:', err);
      }
    };
    fetchDurations();
  }, []);

  // Search free agents
  const searchFreeAgents = async (query) => {
    setSearchingPlayers(true);
    try {
      const res = await usersApi.getFreeAgents(query || undefined);
      setFreeAgents(res.data);
    } catch (err) {
      console.error('Error searching free agents:', err);
      toast.error(isItalian ? 'Errore nella ricerca' : 'Search error');
    } finally {
      setSearchingPlayers(false);
    }
  };

  // Load free agents when dialog opens
  useEffect(() => {
    if (showAddPlayer && isCaptain) {
      searchFreeAgents('');
    }
  }, [showAddPlayer, isCaptain]);

  // Debounced search
  useEffect(() => {
    if (!showAddPlayer) return;
    const timer = setTimeout(() => {
      searchFreeAgents(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, showAddPlayer]);

  // Send contract offer
  const handleSendContractOffer = async () => {
    if (!selectedPlayer) return;
    
    setSendingOffer(true);
    try {
      await contractsApi.create({
        player_user_id: selectedPlayer.user_id,
        duration: contractForm.duration,
        message: isItalian 
          ? `${club?.name} ti offre un contratto!` 
          : `${club?.name} is offering you a contract!`
      });
      
      toast.success(isItalian 
        ? `Offerta inviata a ${selectedPlayer.gamertag || selectedPlayer.name}!` 
        : `Offer sent to ${selectedPlayer.gamertag || selectedPlayer.name}!`);
      
      // Reset and close
      setSelectedPlayer(null);
      setContractForm({ duration: '6_months' });
      setSearchQuery('');
      setShowAddPlayer(false);
      
      // Refresh free agents list
      searchFreeAgents('');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore nell\'invio offerta' : 'Failed to send offer'));
    } finally {
      setSendingOffer(false);
    }
  };

  const handleRelease = async () => {
    setReleasing(true);
    try {
      const res = await clubsApi.release();
      toast.success(res.data.message);
      setShowReleaseDialog(false);
      if (refreshUser) refreshUser();
      // Redirect to find-club page
      window.location.href = '/find-club';
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nello svincolo' : 'Release error'));
    } finally {
      setReleasing(false);
    }
  };

  const handleRespondRequest = async (requestId, accept) => {
    if (accept) {
      // Open dialog to accept with contract
      const request = transferRequests.find(r => r.request_id === requestId);
      setSelectedRequest(request);
      setShowAcceptDialog(true);
    } else {
      // Reject directly
      try {
        await transferRequestsApi.respond(requestId, false);
        setTransferRequests((prev) => prev.filter((r) => r.request_id !== requestId));
        toast.success(isItalian ? 'Richiesta rifiutata' : 'Request rejected');
      } catch (error) {
        console.error('Error rejecting request:', error);
        toast.error(error.response?.data?.detail || (isItalian ? 'Errore nel rifiuto' : 'Error rejecting'));
      }
    }
  };
  
  const handleAcceptWithContract = async () => {
    if (!selectedRequest) return;
    
    const requestToProcess = selectedRequest;
    const durationToUse = contractForm.duration;
    
    // Close dialog immediately
    setShowAcceptDialog(false);
    setSelectedRequest(null);
    setContractForm({ duration: '6_months' });
    
    setAcceptingRequest(true);
    try {
      // First accept the transfer request
      await transferRequestsApi.respond(requestToProcess.request_id, true);
      
      // Then send contract offer
      try {
        await contractsApi.create({
          player_user_id: requestToProcess.player_user_id,
          duration: durationToUse,
        });
        toast.success(isItalian ? 'Giocatore accettato e contratto inviato!' : 'Player accepted and contract sent!');
      } catch (contractError) {
        // Player accepted but contract failed - still show partial success
        console.error('Error creating contract:', contractError);
        toast.success(isItalian ? 'Giocatore accettato!' : 'Player accepted!');
        toast.error(isItalian ? 'Errore invio contratto, riprova dalla pagina Contratti' : 'Contract send failed, retry from Contracts page');
      }
      
      setTransferRequests((prev) => prev.filter((r) => r.request_id !== requestToProcess.request_id));
      await fetchClubData();
    } catch (error) {
      console.error('Error accepting request:', error);
      let errorMessage = isItalian ? 'Errore' : 'Error';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || String(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setAcceptingRequest(false);
    }
  };

  const handleReleasePlayer = async () => {
    if (!playerToRelease || !club) return;
    
    setReleasingPlayer(true);
    try {
      const res = await clubsApi.releasePlayer(club.club_id, playerToRelease.user_id);
      toast.success(res.data.message);
      setShowReleasePlayerDialog(false);
      setPlayerToRelease(null);
      await fetchClubData();
    } catch (error) {
      console.error('Error releasing player:', error);
      let errorMessage = isItalian ? 'Errore nello svincolo' : 'Error releasing player';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setReleasingPlayer(false);
    }
  };

  const handleSetViceCaptain = async (playerId) => {
    if (!club) return;
    
    setSettingViceCaptain(true);
    try {
      const res = await clubsApi.setViceCaptain(club.club_id, playerId);
      toast.success(res.data.message);
      await fetchClubData();
    } catch (error) {
      console.error('Error setting vice captain:', error);
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nella nomina' : 'Error appointing vice captain'));
    } finally {
      setSettingViceCaptain(false);
    }
  };

  const handleRemoveViceCaptain = async (playerId) => {
    if (!club) return;
    
    setSettingViceCaptain(true);
    try {
      const res = await clubsApi.removeViceCaptain(club.club_id, playerId);
      toast.success(res.data.message);
      await fetchClubData();
    } catch (error) {
      console.error('Error removing vice captain:', error);
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nella rimozione' : 'Error removing vice captain'));
    } finally {
      setSettingViceCaptain(false);
    }
  };

  const handleCreateClub = async (e) => {
    e.preventDefault();
    try {
      const res = await clubsApi.create({
        name: clubForm.name,
        colors: { primary: clubForm.primaryColor, secondary: clubForm.secondaryColor },
      });
      setClub(res.data);
      await refreshUser();
      setShowCreateClub(false);
      toast.success('Club created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione club');
    }
  };

  const doUploadLogo = async (file) => {
    try {
      const res = await clubsApi.uploadLogo(club.club_id, file);
      setClub((prev) => ({ ...prev, logo: res.data.logo }));
      setLogoChangeCount(prev => prev + 1);
      const msg = res.data?.message || 'Logo uploaded!';
      toast.success(msg);
      if (res.data?.cost > 0 && refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore caricamento logo');
    } finally {
      setPendingLogoFile(null);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !club) return;

    if (logoChangeCount >= 1 && user?.role !== 'super_admin' && user?.role !== 'admin') {
      setPendingLogoFile(file);
      setShowLogoCostDialog(true);
    } else {
      await doUploadLogo(file);
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      const res = await playersApi.create(playerForm);
      setPlayers((prev) => [...prev, res.data]);
      setShowAddPlayer(false);
      setPlayerForm({ name: '', position: 'MID', number: 10 });
      toast.success('Player added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta giocatore');
    }
  };

  const toggleForSale = async (playerId, currentPrice) => {
    try {
      const res = await playersApi.toggleForSale(playerId, currentPrice);
      setPlayers((prev) =>
        prev.map((p) =>
          p.player_id === playerId ? { ...p, is_for_sale: res.data.is_for_sale, value: res.data.value } : p
        )
      );
      toast.success(res.data.is_for_sale ? 'Player listed for sale' : 'Player removed from market');
    } catch (error) {
      toast.error('Errore aggiornamento stato giocatore');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // No club - show create form
  if (!club) {
    return (
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0a0d15] border border-white/10  p-8 text-center"
        >
          <Shield size={64} className="mx-auto text-[#00F0FF] mb-6" />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-4">
            {t('dashboard.createClub')}
          </h1>
          <p className="text-slate-400 mb-8">
            Create your club to start building your team and competing in championships.
          </p>

          <Dialog open={showCreateClub} onOpenChange={setShowCreateClub}>
            <DialogTrigger asChild>
              <Button className="btn-primary px-8" data-testid="open-create-club">
                <Plus size={18} className="mr-2" />
                {t('dashboard.createClub')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase tracking-wider">
                  {t('dashboard.createClub')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateClub} className="space-y-6 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                    {t('club.name')}
                  </Label>
                  <Input
                    value={clubForm.name}
                    onChange={(e) => setClubForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="bg-[#050508] border-white/10 text-white "
                    placeholder="FC Thunder"
                    required
                    data-testid="club-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                      Primary Color
                    </Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={clubForm.primaryColor}
                        onChange={(e) => setClubForm((prev) => ({ ...prev, primaryColor: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                        data-testid="primary-color-input"
                      />
                      <Input
                        value={clubForm.primaryColor}
                        onChange={(e) => setClubForm((prev) => ({ ...prev, primaryColor: e.target.value }))}
                        className="bg-[#050508] border-white/10 text-white  font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                      Secondary Color
                    </Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={clubForm.secondaryColor}
                        onChange={(e) => setClubForm((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                        data-testid="secondary-color-input"
                      />
                      <Input
                        value={clubForm.secondaryColor}
                        onChange={(e) => setClubForm((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                        className="bg-[#050508] border-white/10 text-white  font-mono"
                      />
                    </div>
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="submit-create-club">
                  {t('dashboard.createClub')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    );
  }

  // Club exists - show club page
  return (
    <div className="space-y-6">
      {/* Club Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0a0d15] border border-white/10  p-6"
        data-testid="club-header"
      >
        {club.premium?.active && club.banner_url && (
          <img src={club.banner_url} alt="banner" className="w-full h-32 md:h-44 object-cover rounded mb-4 border border-[#FFD700]/20" data-testid="club-banner" />
        )}
        {club.premium?.active && (isCaptain || user?.role === 'super_admin') && (
          <label className="inline-flex items-center gap-1 text-[10px] text-[#FFD700] cursor-pointer mb-2 hover:underline">
            <Upload size={12} />
            {isItalian ? (club.banner_url ? 'Cambia banner' : 'Carica banner Premium') : 'Upload banner'}
            <input type="file" accept="image/*" className="hidden" data-testid="upload-banner-input"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  await premiumApi.uploadBanner(club.club_id, f);
                  toast.success(isItalian ? 'Banner aggiornato!' : 'Banner updated!');
                  fetchClubData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Errore');
                }
              }} />
          </label>
        )}
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
          {/* Logo */}
          <div className="relative group">
            <div
              className="w-24 h-24 md:w-32 md:h-32 rounded border border-white/10 flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: club.colors?.primary || '#2979FF' }}
            >
              {club.logo ? (
                <img src={club.logo} alt={club.name} className="w-full h-full object-cover" />
              ) : (
                <Shield size={48} className="text-white/80" />
              )}
            </div>
            {isCaptain && (
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded">
                <Upload size={24} className="text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadLogo}
                  className="hidden"
                  data-testid="upload-logo-input"
                />
              </label>
            )}
            {!isCaptain && user?.role === 'super_admin' && (
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded">
                <Upload size={24} className="text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadLogo}
                  className="hidden"
                />
              </label>
            )}
          </div>
          {isCaptain && (
            <p className="text-[10px] text-center mt-1">
              {logoChangeCount >= 1
                ? <span className="text-[#FFEA00]"><Coins size={10} className="inline mr-0.5" />{LOGO_CHANGE_COST.toLocaleString()} coins</span>
                : <span className="text-[#00E676]">{isItalian ? 'Primo cambio gratis' : 'First change free'}</span>
              }
            </p>
          )}

          {/* Club Info */}
          <div className="flex-1">
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white flex items-center gap-3 flex-wrap">
              {club.name}
              {club.premium?.active && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 bg-[#FFD700]/15 border border-[#FFD700]/50 text-[#FFD700] rounded-full tracking-wider" data-testid="club-premium-badge" title="Club Premium">
                  <Crown size={14} /> PREMIUM
                </span>
              )}
            </h1>
            <div className="flex flex-wrap gap-4 mt-4">
              <div className="flex items-center gap-2 bg-[#050508] border border-white/10  px-4 py-2">
                <Coins size={18} className="text-[#FFEA00]" />
                <span className="font-mono font-bold text-white">{club.coins?.toLocaleString()}</span>
                <span className="text-xs text-slate-500 uppercase">{t('dashboard.coins')}</span>
              </div>
              <div className="flex items-center gap-2 bg-[#050508] border border-white/10  px-4 py-2">
                <Users size={18} className="text-[#00F0FF]" />
                <span className="font-mono font-bold text-white">{players.length}</span>
                <span className="text-xs text-slate-500 uppercase">{t('club.players')}</span>
              </div>
              {/* Contract expiry for non-captain players */}
              {!isCaptain && user?.club_id && (
                <div
                  className={`flex items-center gap-2 bg-[#050508] border px-4 py-2 ${contractExpired ? 'border-[#FF1744]/40' : 'border-white/10'}`}
                  data-testid="my-contract-chip"
                >
                  <FileText size={18} className={contractExpired ? 'text-[#FF1744]' : 'text-[#00E676]'} />
                  <span className={`text-xs uppercase font-bold ${contractExpired ? 'text-[#FF1744]' : 'text-slate-300'}`}>
                    {contractExpired
                      ? (isItalian ? 'Contratto scaduto' : 'Contract expired')
                      : myContractEnd
                        ? (isItalian ? 'Contratto fino al ' : 'Contract until ') + new Date(myContractEnd).toLocaleDateString(isItalian ? 'it-IT' : 'en-GB')
                        : (isItalian ? 'Nessuna scadenza contratto' : 'No contract expiry')}
                  </span>
                </div>
              )}
              {/* Release button for non-captain players */}
              {!isCaptain && user?.club_id && (
                <Button
                  variant="outline"
                  className="border-[#FF1744]/30 text-[#FF1744] hover:bg-[#FF1744]/10"
                  onClick={() => setShowReleaseDialog(true)}
                  data-testid="release-btn"
                >
                  <LogOut size={18} className="mr-2" />
                  {isItalian ? 'Svincolati' : 'Release'}
                  {contractExpired && <span className="ml-2 text-[10px] text-[#00E676] font-bold">{isItalian ? 'GRATIS' : 'FREE'}</span>}
                </Button>
              )}
            </div>
          </div>

          {/* Add Player Button */}
          {isCaptain && (
            <Dialog open={showAddPlayer} onOpenChange={(open) => {
              setShowAddPlayer(open);
              if (!open) {
                setSelectedPlayer(null);
                setSearchQuery('');
                setContractForm({ duration: '6_months' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-player-btn">
                  <UserPlus size={18} className="mr-2" />
                  {t('club.addPlayer')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
                    <UserPlus size={20} className="text-[#00F0FF]" />
                    {isItalian ? 'Recluta Giocatore' : 'Recruit Player'}
                  </DialogTitle>
                </DialogHeader>
                
                {!selectedPlayer ? (
                  // Step 1: Search and select player
                  <div className="space-y-4 mt-4 flex-1 overflow-hidden flex flex-col">
                    {/* Search Input */}
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-[#050508] border-white/10 text-white  pl-10"
                        placeholder={isItalian ? 'Cerca per gamertag o nome...' : 'Search by gamertag or name...'}
                        data-testid="search-player-input"
                      />
                    </div>
                    
                    {/* Free Agents List */}
                    <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[400px] border border-white/10 ">
                      {searchingPlayers ? (
                        <div className="flex items-center justify-center h-32">
                          <div className="w-6 h-6 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : freeAgents.length > 0 ? (
                        <div className="divide-y divide-white/10">
                          {freeAgents.map((agent) => (
                            <div
                              key={agent.user_id}
                              className="p-4 hover:bg-white/5 cursor-pointer transition-colors flex items-center justify-between"
                              onClick={() => setSelectedPlayer(agent)}
                              data-testid={`free-agent-${agent.user_id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#050508] border border-white/10 flex items-center justify-center overflow-hidden">
                                  {agent.picture ? (
                                    <img src={agent.picture} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <Gamepad2 size={18} className="text-[#00F0FF]" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-white">{agent.gamertag || agent.name}</p>
                                  {agent.playing_position && (
                                    <p className="text-xs text-slate-400">{agent.playing_position}</p>
                                  )}
                                </div>
                              </div>
                              <Button size="sm" variant="ghost" className="text-[#00F0FF]">
                                <Send size={16} className="mr-1" />
                                {isItalian ? 'Offri' : 'Offer'}
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                          <Users size={32} className="mb-2 opacity-50" />
                          <p>{isItalian ? 'Nessun giocatore libero trovato' : 'No free agents found'}</p>
                          <p className="text-xs mt-1">{isItalian ? 'Prova a cercare con un altro termine' : 'Try a different search term'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Step 2: Contract offer form
                  <div className="space-y-6 mt-4">
                    {/* Selected Player Info */}
                    <div className="p-4 bg-[#050508] border border-[#00F0FF]/30 ">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-[#0a0d15] border border-white/10 flex items-center justify-center overflow-hidden">
                          {selectedPlayer.picture ? (
                            <img src={selectedPlayer.picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Gamepad2 size={24} className="text-[#00F0FF]" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-white text-lg">{selectedPlayer.gamertag || selectedPlayer.name}</p>
                          {selectedPlayer.playing_position && (
                            <p className="text-sm text-slate-400">{selectedPlayer.playing_position}</p>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setSelectedPlayer(null)}
                          className="text-slate-400 hover:text-white"
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Contract Form */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold flex items-center gap-2">
                          <FileText size={14} />
                          {isItalian ? 'Durata Contratto' : 'Contract Duration'}
                        </Label>
                        <Select
                          value={contractForm.duration}
                          onValueChange={(value) => setContractForm((prev) => ({ ...prev, duration: value }))}
                        >
                          <SelectTrigger className="bg-[#050508] border-white/10 text-white " data-testid="contract-duration-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a0d15] border-white/10">
                            {contractDurations.map((dur) => (
                              <SelectItem key={dur.id} value={dur.id}>{dur.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1 border-white/10"
                        onClick={() => setSelectedPlayer(null)}
                        disabled={sendingOffer}
                      >
                        {isItalian ? 'Indietro' : 'Back'}
                      </Button>
                      <Button
                        className="flex-1 bg-[#00E676] hover:bg-[#00E676]/80 text-black font-bold"
                        onClick={handleSendContractOffer}
                        disabled={sendingOffer}
                        data-testid="send-contract-offer-btn"
                      >
                        {sendingOffer ? (
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send size={16} className="mr-2" />
                            {isItalian ? 'Invia Offerta' : 'Send Offer'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </motion.div>

      {/* Roster */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#0a0d15] border border-white/10 "
        data-testid="roster-section"
      >
        <div className="p-4 border-b border-white/10 flex flex-wrap items-center justify-between gap-4">
          <h2 className="font-heading text-xl font-semibold uppercase tracking-wider text-white">
            {t('club.roster')}
          </h2>
        </div>

        {players.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Giocatore</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Ruolo' : 'Role'}</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players.map((player) => {
                const isViceCaptain = [club?.vice_captain_id, club?.vice_captain_id_2].includes(player.user_id);
                
                return (
                <TableRow
                  key={player.user_id}
                  className="border-white/10 hover:bg-white/5"
                  data-testid={`player-row-${player.user_id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#050508] border border-white/10 flex items-center justify-center overflow-hidden">
                        {player.picture ? (
                          <img src={player.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Gamepad2 size={18} className="text-[#00F0FF]" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {player.gamertag ? `${player.gamertag}` : player.name}
                        </p>
                        {player.is_captain && (
                          <span className="text-xs text-[#FFEA00] flex items-center gap-1">
                            <Crown size={12} />
                            {isItalian ? 'Capitano' : 'Captain'}
                          </span>
                        )}
                        {isViceCaptain && !player.is_captain && (
                          <span className="text-xs text-[#00E676] flex items-center gap-1">
                            <Star size={12} />
                            {isItalian ? 'Vice Capitano' : 'Vice Captain'}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {(player.player_role || player.playing_position) ? (
                      <span className="px-2 py-1 text-xs rounded bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30 font-semibold">
                        {player.player_role || player.playing_position}
                      </span>
                    ) : (
                      <span className="text-slate-500 text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {player.is_captain ? (
                        <span className="px-2 py-1 text-xs rounded bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30">
                          {isItalian ? 'Capitano' : 'Captain'}
                        </span>
                      ) : isViceCaptain ? (
                        <>
                          <span className="px-2 py-1 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
                            {isItalian ? 'Vice' : 'Vice'}
                          </span>
                          {(isCaptain || club?.captain_id === user?.user_id) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10"
                              onClick={() => handleRemoveViceCaptain(player.user_id)}
                              disabled={settingViceCaptain}
                              data-testid={`remove-vice-${player.user_id}`}
                            >
                              <X size={14} />
                            </Button>
                          )}
                        </>
                      ) : ((isCaptain || club?.captain_id === user?.user_id) || isViceUser || user?.role === 'super_admin') ? (
                        <div className="flex items-center gap-2">
                          {(isCaptain || club?.captain_id === user?.user_id) && !(club?.vice_captain_id && club?.vice_captain_id_2) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-[#00E676] border-[#00E676]/50 hover:bg-[#00E676]/10"
                              onClick={() => handleSetViceCaptain(player.user_id)}
                              disabled={settingViceCaptain}
                              data-testid={`set-vice-${player.user_id}`}
                              title={isItalian ? 'Nomina Vice Capitano' : 'Appoint Vice Captain'}
                            >
                              <Star size={14} className="mr-1" />
                              {isItalian ? 'Vice' : 'Vice'}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10"
                            onClick={() => {
                              setPlayerToRelease(player);
                              setShowReleasePlayerDialog(true);
                            }}
                            data-testid={`release-player-${player.user_id}`}
                          >
                            <UserMinus size={14} className="mr-1" />
                            {isItalian ? 'Svincola' : 'Release'}
                          </Button>
                        </div>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
                          {isItalian ? 'Attivo' : 'Active'}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        ) : (
          <div className="p-8 text-center">
            <AlertCircle size={32} className="mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500">{t('common.noData')}</p>
            {isCaptain && (
              <Button
                variant="link"
                className="text-[#00F0FF] mt-2"
                onClick={() => setShowAddPlayer(true)}
              >
                Add your first player
              </Button>
            )}
          </div>
        )}
      </motion.div>

      {/* EA Pro Clubs Stats */}
      {club.premium?.active && <ClubAdvancedStats clubId={club.club_id} isItalian={isItalian} />}
      <EAStatsSection
        club={club}
        canManage={isCaptain || user?.role === 'super_admin'}
        isItalian={isItalian}
      />

      {/* Transfer Requests Section - Captain, Vice Captain or Super Admin */}
      {(isCaptain || isViceUser || user?.role === 'super_admin') && club && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#0a0d15] border border-white/10 "
          data-testid="transfer-requests-section"
        >
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserPlus size={20} className="text-[#00E676]" />
              <h2 className="font-heading text-xl font-semibold uppercase tracking-wider text-white">
                {isItalian ? 'Richieste di Trasferimento' : 'Transfer Requests'}
              </h2>
            </div>
            {transferRequests.length > 0 && (
              <span className="px-2 py-1 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
                {transferRequests.length} {isItalian ? 'nuove' : 'new'}
              </span>
            )}
          </div>

          {transferRequests.length > 0 ? (
            <div className="divide-y divide-white/10">
              {transferRequests.map((req) => (
                <div
                  key={req.request_id}
                  className="p-4 flex items-center justify-between hover:bg-white/5"
                  data-testid={`transfer-request-${req.request_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#050508] border border-white/10 flex items-center justify-center">
                      <Gamepad2 size={20} className="text-[#00F0FF]" />
                    </div>
                    <div>
                      <p className="font-medium text-white">
                        {req.player_gamertag ? `${req.player_gamertag}` : req.player_name}
                      </p>
                      {req.message && (
                        <p className="text-xs text-slate-400 mt-1 max-w-md truncate">"{req.message}"</p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(req.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-[#00E676] hover:bg-[#00E676]/80 text-black"
                      onClick={() => handleRespondRequest(req.request_id, true)}
                      data-testid={`accept-request-${req.request_id}`}
                    >
                      <Check size={16} className="mr-1" />
                      {isItalian ? 'Accetta' : 'Accept'}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleRespondRequest(req.request_id, false)}
                      data-testid={`reject-request-${req.request_id}`}
                    >
                      <X size={16} className="mr-1" />
                      {isItalian ? 'Rifiuta' : 'Reject'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <UserPlus size={40} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-500">
                {isItalian 
                  ? 'Nessuna richiesta di trasferimento al momento'
                  : 'No transfer requests at the moment'}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                {isItalian 
                  ? 'Quando un giocatore richiede di unirsi al tuo club, lo vedrai qui'
                  : 'When a player requests to join your club, you will see it here'}
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* Release Confirmation Dialog */}
      <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF1744]">
              {isItalian ? 'Svincolati dal Club' : 'Release from Club'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30  text-center">
              <p className="text-white">
                {isItalian 
                  ? `Vuoi svincolarti da ${club?.name}?`
                  : `Do you want to release from ${club?.name}?`}
              </p>
              <p className="text-sm text-slate-400 mt-2">
                {isItalian 
                  ? 'Questa azione ti rimuoverà dal club.'
                  : 'This will remove you from the club.'}
              </p>
            </div>
            
            <div className="p-4  bg-[#FFEA00]/10 border border-[#FFEA00]/30">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 uppercase text-xs font-bold">
                  {isItalian ? 'Costo Svincolo' : 'Release Cost'}
                </span>
                <div className="flex items-center gap-2">
                  {releaseCost === 0 ? (
                    <span className="font-mono text-xl font-bold text-[#00E676]">{isItalian ? 'GRATIS' : 'FREE'}</span>
                  ) : (
                    <>
                      <Coins size={18} className="text-[#FFEA00]" />
                      <span className="font-mono text-xl font-bold text-[#FFEA00]">
                        {releaseCost.toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {releaseCost === 0
                  ? (isItalian ? 'Il tuo contratto è scaduto: nessun costo.' : 'Your contract has expired: no cost.')
                  : (isItalian ? 'I coins verranno detratti dal tuo saldo personale.' : 'Coins will be deducted from your personal balance.')}
              </p>
              
              {/* Show personal coins balance */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">{isItalian ? 'I Tuoi Coins' : 'Your Coins'}</span>
                  <span className="font-mono text-white">{(user?.personal_coins || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {/* Not enough coins warning */}
            {(user?.personal_coins || 0) < releaseCost && (
              <div className="p-3 bg-[#FF1744]/10 border border-[#FF1744]/30 ">
                <p className="text-sm text-[#FF1744] text-center">
                  {isItalian 
                    ? `Non hai abbastanza coins personali (${(user?.personal_coins || 0).toLocaleString()} / ${releaseCost.toLocaleString()})` 
                    : `Not enough personal coins (${(user?.personal_coins || 0).toLocaleString()} / ${releaseCost.toLocaleString()})`}
                </p>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-white/10"
                onClick={() => setShowReleaseDialog(false)}
                disabled={releasing}
              >
                {isItalian ? 'Annulla' : 'Cancel'}
              </Button>
              <Button
                className="flex-1 bg-[#FF1744] hover:bg-[#FF1744]/80"
                onClick={handleRelease}
                disabled={releasing || (user?.personal_coins || 0) < releaseCost}
                data-testid="confirm-release-btn"
              >
                {releasing ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogOut size={16} className="mr-2" />
                    {isItalian ? 'Svincolati' : 'Release'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Accept Transfer with Contract Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#00E676]">
              {isItalian ? 'Accetta Giocatore' : 'Accept Player'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              {/* Player Info */}
              <div className="p-4 bg-[#050508] border border-white/10 ">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#00F0FF]/20 border border-[#00F0FF]/30 flex items-center justify-center">
                    <Gamepad2 size={24} className="text-[#00F0FF]" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-lg">
                      {selectedRequest.player_gamertag ? `${selectedRequest.player_gamertag}` : selectedRequest.player_name}
                    </p>
                    {selectedRequest.message && (
                      <p className="text-sm text-slate-400">"{selectedRequest.message}"</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Contract Duration Selection */}
              <div className="space-y-2">
                <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                  {isItalian ? 'Durata Contratto' : 'Contract Duration'}
                </Label>
                <Select 
                  value={contractForm.duration} 
                  onValueChange={(val) => setContractForm({ ...contractForm, duration: val })}
                  modal={false}
                >
                  <SelectTrigger className="bg-[#050508] border-white/10 text-white">
                    <SelectValue placeholder={isItalian ? 'Seleziona durata...' : 'Select duration...'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0d15] border-white/10 z-[100]">
                    {contractDurations.map((dur) => (
                      <SelectItem key={dur.id} value={dur.id} className="text-white hover:bg-white/10 cursor-pointer">{dur.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-[#00E676]/10 border border-[#00E676]/30 ">
                <p className="text-sm text-center text-[#00E676]">
                  {isItalian 
                    ? 'Il giocatore entrerà nel club e riceverà un\'offerta di contratto'
                    : 'The player will join the club and receive a contract offer'}
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/10"
                  onClick={() => {
                    setShowAcceptDialog(false);
                    setSelectedRequest(null);
                  }}
                  disabled={acceptingRequest}
                >
                  {isItalian ? 'Annulla' : 'Cancel'}
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-[#00E676] to-[#00C853] hover:opacity-90 text-black font-bold"
                  onClick={handleAcceptWithContract}
                  disabled={acceptingRequest || !contractForm.duration}
                  data-testid="confirm-accept-btn"
                >
                  {acceptingRequest ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Check size={16} className="mr-2" />
                      {isItalian ? 'Accetta e Offri Contratto' : 'Accept & Offer Contract'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Release Player Confirmation Dialog */}
      <Dialog open={showReleasePlayerDialog} onOpenChange={setShowReleasePlayerDialog}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF1744]">
              {isItalian ? 'Svincola Giocatore' : 'Release Player'}
            </DialogTitle>
          </DialogHeader>
          
          {playerToRelease && (
            <div className="space-y-4 mt-4">
              {/* Player Info */}
              <div className="p-4 bg-[#050508] border border-white/10 ">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#FF1744]/20 border border-[#FF1744]/30 flex items-center justify-center">
                    <Gamepad2 size={24} className="text-[#FF1744]" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-lg">
                      {playerToRelease.gamertag ? `${playerToRelease.gamertag}` : playerToRelease.name}
                    </p>
                    {playerToRelease.playing_position && (
                      <p className="text-sm text-slate-400">{playerToRelease.playing_position}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30  text-center">
                <p className="text-white">
                  {isItalian 
                    ? `Vuoi svincolare questo giocatore da ${club?.name}?`
                    : `Do you want to release this player from ${club?.name}?`}
                </p>
                <p className="text-sm text-slate-400 mt-2">
                  {isItalian 
                    ? 'Il giocatore diventerà un free agent.'
                    : 'The player will become a free agent.'}
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/10"
                  onClick={() => {
                    setShowReleasePlayerDialog(false);
                    setPlayerToRelease(null);
                  }}
                  disabled={releasingPlayer}
                >
                  {isItalian ? 'Annulla' : 'Cancel'}
                </Button>
                <Button
                  className="flex-1 bg-[#FF1744] hover:bg-[#FF1744]/80"
                  onClick={handleReleasePlayer}
                  disabled={releasingPlayer}
                  data-testid="confirm-release-player-btn"
                >
                  {releasingPlayer ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <UserMinus size={16} className="mr-2" />
                      {isItalian ? 'Svincola' : 'Release'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Logo Cost Confirmation Dialog */}
      {showLogoCostDialog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setShowLogoCostDialog(false); setPendingLogoFile(null); }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#0a0d15] border border-white/10 rounded-lg p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-[#FFEA00]/20 flex items-center justify-center"><Coins size={20} className="text-[#FFEA00]" /></div>
              <h3 className="font-heading text-lg uppercase tracking-wider text-white">{isItalian ? 'Conferma Cambio Logo' : 'Confirm Logo Change'}</h3>
            </div>
            <div className="p-4 bg-[#050508] border border-[#FFEA00]/20 rounded mb-4">
              <p className="text-white text-center text-sm">
                {isItalian
                  ? `Cambiare il logo del club costa ${LOGO_CHANGE_COST.toLocaleString()} coins.`
                  : `Changing the club logo costs ${LOGO_CHANGE_COST.toLocaleString()} coins.`}
              </p>
              <p className="text-xs text-slate-400 text-center mt-2">
                {isItalian ? 'I coins verranno scalati dal tuo saldo personale.' : 'Coins will be deducted from your personal balance.'}
              </p>
              <div className="flex justify-center mt-3">
                <span className="text-sm text-slate-300">{isItalian ? 'Saldo:' : 'Balance:'} <span className={`font-mono font-bold ${(user?.personal_coins || 0) >= LOGO_CHANGE_COST ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>{(user?.personal_coins || 0).toLocaleString()}</span></span>
              </div>
            </div>
            {(user?.personal_coins || 0) < LOGO_CHANGE_COST && (
              <div className="p-3 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded mb-4">
                <p className="text-xs text-[#FF1744] text-center">{isItalian ? 'Coins insufficienti!' : 'Insufficient coins!'}</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-white/10" onClick={() => { setShowLogoCostDialog(false); setPendingLogoFile(null); }}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
              <Button className="flex-1 bg-[#FFEA00] hover:bg-[#FFEA00]/90 text-black font-bold" disabled={(user?.personal_coins || 0) < LOGO_CHANGE_COST} onClick={() => { setShowLogoCostDialog(false); if (pendingLogoFile) doUploadLogo(pendingLogoFile); }} data-testid="confirm-logo-cost-btn">
                <Coins size={14} className="mr-2" />{isItalian ? 'Paga e Cambia' : 'Pay & Change'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Club;
